<template>
  <div>
    <NavBar />
    <div class="container-fluid px-5 mb-4">
      <div class="row">
        <div class="col">
          <h1>
            {{ title }}
            <span class="badge rounded mx-2" :class="statusCssClass">{{ investigation.statusShortDescription }}</span>
          </h1>
        </div>
        <div class="col-auto ml-auto">
          <CloseButton v-if="showCloseButton" :investigation="investigation" :isLoading="isLoading" @reload="load" />
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div v-if="successMessage" class="alert alert-success alert-dismissible fade show mt-4" role="alert">
            {{ successMessage }}
            <button type="button" class="close" @click="successMessage = null" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <ErrorSummary :error="error" />
        </div>
      </div>
      <div class="row mt-4" v-if="showNonProctorSections">
        <div class="col">
          <Description :investigation="investigation" :isLoading="isLoading" @reload="load" />
        </div>
      </div>
      <div class="row mt-4" v-if="showNonProctorSections">
        <div class="col">
          <Attributes :investigation="investigation" :isLoading="isLoading" @reload="load" />
        </div>
      </div>
      <div class="row mt-4" v-if="showNonProctorSections">
        <div class="col">
          <Collaborators :investigation="investigation" :isLoading="isLoading" @reload="load" />
        </div>
      </div>
      <div class="row mt-4" v-if="showNonProctorSections">
        <div class="col">
          <InterviewDetails :investigation="investigation" :isLoading="isLoading" @reload="load" />
        </div>
      </div>

      <div class="row mt-4" v-if="showNonProctorSections">
        <div class="col">
          <InterviewStatistics :investigation="investigation" :isLoading="isLoading" @reload="load" />
        </div>
      </div>
      <div class="row mt-4" v-if="showNonProctorSections">
        <div class="col">
          <InterviewBuckets :investigation="investigation" :isLoading="isLoading" @reload="load" @star-updated="handleStarUpdate" />
        </div>
      </div>
      <div class="row mt-4" v-if="showNonProctorSections">
        <div class="col">
          <InvestigationRiskIndexDetail
            :investigation="investigation"
            :organizationId="organizationId"
            :investigationId="investigationId"
            :isLoading="isLoading"
            @star-updated="handleStarUpdate"
          />
        </div>
      </div>
      <div class="row mt-4">
        <div class="col">
          <Interviews ref="interviews" :investigation="investigation" :isLoading="isLoading" @reload="load" @star-updated="handleStarUpdate" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import NavBar from "@/views/reports/NavBar.vue";
import ErrorSummary from "@/components/ErrorSummary.vue";
import formatter from "@/utils/formatter";
import Description from "@/views/reports/investigations/edit-investigation/Description.vue";
import Attributes from "@/views/reports/investigations/edit-investigation/Attributes.vue";
import Collaborators from "@/views/reports/investigations/edit-investigation/Collaborators.vue";
import InterviewDetails from "@/views/reports/investigations/edit-investigation/InterviewDetails.vue";
import InterviewStatistics from "@/views/reports/investigations/edit-investigation/InterviewStatistics.vue";
import InterviewBuckets from "@/views/reports/investigations/edit-investigation/InterviewBuckets.vue";
import Interviews from "@/views/reports/investigations/edit-investigation/Interviews.vue";
import CloseButton from "@/views/reports/investigations/edit-investigation/CloseButton.vue";
import InvestigationRiskIndexDetail from "@/views/reports/investigations/edit-investigation/InvestigationRiskIndexDetail.vue";

export default {
  components: {
    NavBar,
    ErrorSummary,
    Description,
    Attributes,
    Collaborators,
    InterviewDetails,
    Interviews,
    InterviewStatistics,
    InterviewBuckets,
    CloseButton,
    InvestigationRiskIndexDetail,
  },
  props: {
    organizationId: String,
    investigationId: String,
  },
  data() {
    return {
      loadingMessage: null,
      successMessage: null,
      error: null,

      investigation: {},
    };
  },
  computed: {
    ...mapGetters("role", ["isVerensicsAdministrator", "isOrganizationAdministrator", "isInvestigationCreator", "isInvestigationProctor"]),
    formatter: () => formatter,
    isLoading() {
      return !!this.loadingMessage;
    },
    isOpen() {
      return this.investigation.statusCode === "open";
    },
    isClosed() {
      return this.investigation.statusCode === "closed";
    },
    showCloseButton() {
      return this.isVerensicsAdministrator || this.isOrganizationAdministrator || this.isInvestigationCreator;
    },
    showNonProctorSections() {
      // if any non-proctor roles, show all sections
      if (this.isVerensicsAdministrator || this.isOrganizationAdministrator || this.isInvestigationCreator || this.isInvestigationViewer) return true;
      // If only a proctor, hide non-proctor sections
      if (this.isInvestigationProctor) return false;
      // default to hide a section
      return false;
    },
    title() {
      if (!this.investigation) return null;
      if (this.investigation.name) return this.investigation.name;
      return null;
    },
    statusCssClass() {
      if (this.isOpen) return "badge-info";
      if (this.isClosed) return "badge-success";
      return "badge-secondary";
    },
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      this.loadingMessage = "Loading...";
      this.$axios
        .get(`/v1/organizations/${this.organizationId}/investigations/${this.investigationId}`)
        .then(res => (this.investigation = res.data))
        .catch(err => {
          if (err.response.status === 404) this.$router.push(`/organizations/${this.organizationId}/investigations`);
          this.error = err;
        })
        .finally(() => (this.loadingMessage = null));
    },
    handleStarUpdate(updatedInterview) {
      // Propagate star state changes to InterviewBuckets, InvestigationRiskIndexDetail, and Interviews components

      // Update the investigation data if it contains interview buckets
      if (this.investigation && this.investigation.interviewBuckets) {
        Object.keys(this.investigation.interviewBuckets).forEach(bucketKey => {
          const bucket = this.investigation.interviewBuckets[bucketKey];
          if (bucket && bucket.interviews) {
            const interview = bucket.interviews.find(i => i.organizationReportId === updatedInterview.organizationReportId);
            if (interview) {
              interview.isStarred = updatedInterview.isStarred;
            }
          }
        });
      }

      // Update the risk index analysis data if it exists
      if (this.investigation && this.investigation.riskIndexAnalysis && this.investigation.riskIndexAnalysis.categories) {
        this.investigation.riskIndexAnalysis.categories.forEach(category => {
          if (category.questions) {
            category.questions.forEach(question => {
              if (question.reports) {
                const report = question.reports.find(r => r.organizationReportId === updatedInterview.organizationReportId);
                if (report) {
                  report.isStarred = updatedInterview.isStarred;
                }
              }
            });
          }
        });
      }

      // Update the Interviews component if it exists
      if (this.$refs.interviews && this.$refs.interviews.updateInterviewStar) {
        this.$refs.interviews.updateInterviewStar(updatedInterview);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
h1 {
  .badge {
    font-size: 40%;
    vertical-align: super;
  }
}
</style>
